import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, ShieldCheck, Users, Home as HomeIcon } from "lucide-react";
import Layout, { PageHero } from "../components/site/Layout";
import { SectionTitle, Reveal, Eyebrow } from "../components/site/common";
import { ILLUS, PHOTOS } from "../lib/data";

const PILLARS = [
  [ShieldCheck, "Dignity & Safety", "Championing dignity, safety and respect for senior citizens and vulnerable elderly members of society."],
  [HeartHandshake, "Companionship & Care", "Encouraging compassionate companionship, emotional wellbeing and supportive care for the elderly."],
  [Users, "Social Inclusion", "Promoting inclusion so that senior citizens remain valued and connected within their families and communities."],
  [HomeIcon, "Elder-Care Environments", "Exploring appropriate old age home and elder-care initiatives that offer a supportive, caring environment."],
];

export default function ElderCare() {
  return (
    <Layout>
      <PageHero
        eyebrow="Area of Focus"
        title="Old Age Homes & Senior Citizen Care"
        intro="Supporting the dignity, care, wellbeing and social inclusion of senior citizens, including initiatives related to old age homes, elder care and compassionate community support."
      />

      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-10 lg:py-20">
        <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-6">
            <div className="aspect-square overflow-hidden border border-gold/30 bg-ivory">
              <img src={ILLUS.focus_eldercare} alt="Illustration of elder care and companionship" className="h-full w-full object-contain p-8" />
            </div>
          </div>
          <div className="lg:col-span-6">
            <SectionTitle eyebrow="Our Commitment" title="Caring for those who came before us" />
            <div className="mt-6 space-y-4 text-base leading-relaxed text-ink-muted lg:text-lg">
              <p>Sri Akilandeswari Amman Trust recognises the wellbeing of senior citizens and elderly members of society as a meaningful area of focus. We seek to promote dignity, care, inclusion and compassionate support for the elderly.</p>
              <p>This includes exploring and supporting appropriate old age home and elder-care initiatives that provide dignity, safety, companionship, care and a supportive environment for senior citizens.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Important clarity note */}
      <section className="border-y border-gold/20 bg-white">
        <div className="mx-auto max-w-4xl px-5 py-12 lg:px-10">
          <div className="border-l-2 border-gold pl-6">
            <Eyebrow>Please Note</Eyebrow>
            <p className="mt-3 text-base leading-relaxed text-ink">
              This is an <strong>area of service and a future initiative</strong>. The Trust does not currently own, operate or manage an old age home or residential facility. Verified details of any elder-care initiative will be published here as they are officially established.
            </p>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-ivory-dark/40">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-10 lg:py-20">
          <SectionTitle eyebrow="How We Aim to Serve" title="Guiding principles of elder care" />
          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {PILLARS.map(([Icon, title, desc], i) => (
              <Reveal key={title} delay={(i % 2) * 0.05}>
                <div className="h-full border border-gold/20 bg-white p-7">
                  <Icon className="text-gold" size={26} strokeWidth={1.4} />
                  <h3 className="mt-4 text-xl font-medium text-maroon">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink-muted">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-maroon">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-5 py-16 lg:flex-row lg:items-center lg:justify-between lg:px-10">
          <h2 className="max-w-xl text-3xl font-medium text-ivory lg:text-4xl">Help us build compassionate care for our elders.</h2>
          <Link to="/support" data-testid="eldercare-support-btn" className="inline-flex items-center gap-2 border border-gold-light px-7 py-4 text-xs uppercase tracking-wide text-ivory transition-colors duration-300 hover:bg-ivory/10">
            Register Your Interest <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </Layout>
  );
}
